<script setup lang="ts">
import background from '~/assets/images/backgrounds/settings.jpg';
import type { CategoryGroupType, GameSavingType } from '~/types/tasks';

const mainStore = useMainStore();
const infoStore = useInfoStore();
const gameStore = useGameStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { data: settings } = await useAsyncData('categories', () =>
  TaskAPI.categories(),
);

const selectedGroup = ref<CategoryGroupType>();
const dialog = ref(false);
const visible = ref(false);
const savingDialogVisible = ref(false);
const selectedCategoryHint = ref({ id: 101, visible: false });
const pairedGameDialogVisible = ref(route.query.action === 'paired-game');

const categoryHints = computed<Record<number, string>>(() => ({
  101: t('category_hints.101'),
}));

const showCategoryHint = (itemId: number) => {
  selectedCategoryHint.value = {
    id: itemId,
    visible: true,
  };
};

const toggleAll = () => {
  if (!selectedGroup.value) return;

  const directionInclude = !selectedGroup.value.categories.every(
    (el) => el.value,
  );
  selectedGroup.value.categories.forEach((el) => {
    el.value = directionInclude;
  });
};

const onStartGame = () => {
  if (!settings.value) return;

  gameStore.categories = settings.value
    .reduce(
      (acc, el) => [
        ...acc,
        ...(el.value
          ? el.categories.map((c) => {
              if (el.locked && !mainStore.pro) return 0;

              return c.value ? c.id : 0;
            })
          : []),
      ],
      [] as number[],
    )
    .filter((el) => el);

  MetricAPI.create({
    body: {
      type: 'apply_categories',
      data: gameStore.categories,
    },
  });

  localStorage.removeItem('completed_tasks');
  router.push({
    path: '/game',
    query: {
      level: route.query.level,
      mode: infoStore.pairedGame ? 'paired' : undefined,
    },
  });
};

const onLoad = (gameSaving: GameSavingType) => {
  settings.value?.forEach((group) => {
    const el = gameSaving.value.find((f) => f.id === group.id);
    if (el) group.value = el.value;

    group.categories.forEach((c) => {
      const el2 = gameSaving.value.find((f) => f.id === c.id);
      if (el2) c.value = el2.value;
    });
  });
};

useSchemaOrg([
  defineWebPage({
    name: t('schema_title'),
    datePublished: new Date(2022, 4, 27, 20).toISOString(),
  }),
]);
</script>

<template>
  <main
    class="gameSettings pb-4 relative p-section"
    data-test-id="game-settings-page"
  >
    <Head>
      <Title>{{ t('meta.game_settings.title') }}</Title>
      <Meta property="og:title" :content="t('meta.game_settings.title')" />
    </Head>

    <NuxtImg
      class="page-image-preview gameSettings__background"
      :src="background"
      :alt="t('preview_alt')"
      width="1700"
      height="1133"
      sizes="xs:100vw sm:100vw lg:100vw xl:100vw xxl:100vw"
      format="avif"
    />
    <div class="page-overflow-7 gameSettings__background" />

    <div
      class="pt-16 gameSettings__header z-3 flex gap-2 sm:gap-3 mx-auto w-fit mt-5 mb-8"
    >
      <Button
        @click="onStartGame"
        rounded
        size="large"
        class="mx-auto md:px-4"
        data-test-id="submit"
      >
        {{ t('start_the_game') }}
      </Button>
      <Button
        @click="pairedGameDialogVisible = true"
        class="gameSettings__additionalAction"
        icon="pi pi-users"
        size="large"
        rounded
        :disabled="!!infoStore.pairedGame"
      />
      <Button
        @click="savingDialogVisible = true"
        class="gameSettings__additionalAction"
        severity="secondary"
        icon="pi pi-save"
        size="large"
        rounded
        data-test-id="actions-savings"
      />
      <Button
        @click="visible = true"
        class="gameSettings__additionalAction"
        severity="secondary"
        icon="pi pi-question"
        size="large"
        rounded
        :aria-label="t('open_aria_label')"
      />
    </div>
    <div class="gameSettings__content mx-auto pb-12 relative z-2">
      <BaseFreePro class="mb-4" />
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <Card
          v-for="group in settings"
          :key="group.id"
          data-test-id="category-group"
          :data-group-id="group.id"
          class="relative"
          :pt="{
            title: {
              class: {
                'opacity-50 pointer-events-none':
                  group.locked && !mainStore.pro,
              },
            },
          }"
        >
          <template #title>
            <p class="my-0 text-xl md:text-2xl flex items-start">
              <span class="mr-2 flex items-center gap-2">
                {{ t(`categories.groups.${group.id}.label`) }}

                <i
                  v-if="group.locked && !mainStore.pro"
                  class="pi pi-lock text-xl"
                />
              </span>

              <ToggleSwitch
                v-if="!group.readonly"
                class="ml-auto flex-shrink-0"
                v-model="group.value"
                data-test-id="category-group-toggle"
              />
            </p>
          </template>

          <template #content>
            <div class="flex gap-2 justify-center">
              <Button
                v-if="!group.locked || mainStore.pro"
                @click="
                  selectedGroup = group;
                  dialog = true;
                  group.value = true;
                "
                rounded
                outlined
                :label="t('customize')"
                data-test-id="category-group-customize-button"
              />
              <Button
                v-else
                @click="mainStore.isSubscriptionDialogVisible = true"
                :label="t('unblock')"
                rounded
              />
            </div>
          </template>
        </Card>
      </div>
    </div>

    <Dialog
      v-if="selectedGroup"
      v-model:visible="dialog"
      style="width: 420px"
      dismissable-mask
      :draggable="false"
      modal
      data-test-id="customize-group-dialog"
      :pt="{
        pcCloseButton: {
          root: { 'data-test-id': 'customize-group-dialog-close' },
        },
      }"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <span class="p-dialog-title">
            {{ t(`categories.groups.${selectedGroup.id}.label`) }}
          </span>
        </div>
      </template>

      <div class="flex flex-col gap-2">
        <Button
          @click="toggleAll"
          outlined
          class="justify-center mb-3"
          :label="t('switch_all')"
        />
        <template v-for="item in selectedGroup.categories">
          <span
            v-if="!item.deprecated"
            :key="item.id"
            class="my-0 text-2xl flex items-center"
          >
            <span class="mr-1 text-lg w-fit">
              {{ t(`categories.groups.${selectedGroup.id}.items.${item.id}`) }}
            </span>
            <Button
              v-if="categoryHints[item.id]"
              @click="showCategoryHint(item.id)"
              icon="pi pi-question-circle"
              text
              rounded
              class="w-1.5rem h-1.5rem mr-3"
            />
            <ToggleSwitch
              class="ml-auto"
              v-model="item.value"
              data-test-id="toggle-category"
              :data-category-id="item.id"
            />
          </span>
        </template>
      </div>
    </Dialog>

    <DialogPlayInstruction v-model:visible="visible" />

    <GameSettingsSaving
      v-if="settings"
      v-model:visible="savingDialogVisible"
      @load="onLoad"
      :categories="settings"
    />

    <DialogPairedGame v-model:visible="pairedGameDialogVisible" />

    <Dialog
      v-if="selectedGroup"
      :header="
        t('hint_title', {
          title: t(`categories.groups.100.items.${selectedCategoryHint.id}`),
        })
      "
      :visible="selectedCategoryHint.visible"
      @update:visible="selectedCategoryHint.visible = false"
      style="width: 540px"
      dismissable-mask
      :draggable="false"
      modal
    >
      <div class="leading-6 md:py-2">
        {{ categoryHints[selectedCategoryHint.id] }}
      </div>

      <template #footer>
        <Button
          :label="t('close')"
          icon="pi pi-times"
          @click="selectedCategoryHint.visible = false"
          text
        />
      </template>
    </Dialog>
  </main>
</template>

<i18n src="~/locales/meta.yaml"></i18n>
<i18n src="#locales/categories.yaml"></i18n>
<i18n>
en:
  schema_title: New game settings
  preview_alt: Customize the game for you and your significant partner
  start_the_game: Start the game
  customize: Customize
  switch_all: Switch all
  open_aria_label: Open game rules
  close: Close
  hint_title: Description of «{title}»
  category_hints:
    '101': >
      When this setting is enabled, you will sometimes encounter tasks that are slightly outside the categories you have
      selected.

      This will not lead to a significant change in the tasks received, but will add a little variety to the gameplay.

      We recommend leaving the setting enabled for the best gaming experience.
  unblock: Get

ru:
  schema_title: Настройки новой игры
  preview_alt: Настраивайте игру для себя и своей второй половинки
  start_the_game: Начать игру
  customize: Настроить
  switch_all: Переключить все
  open_aria_label: Открыть правила игры
  close: Закрыть
  unblock: Получить
  hint_title: Описание «{title}»
  category_hints:
    '101': |
      При включенной настройке вам будут иногда попадаться задания слегка выходящие за пределы выбранных вами категорий.
      Это не приведет к существенному изменению получаемых заданий, но добавит немного разнообразия в игровой процесс.
      Рекомендуем оставлять настройку включенной для лучшего игрового опыта.

de:
  schema_title: Neue Spieleinstellungen
  preview_alt: Passen Sie das Spiel für sich und Ihren Lebensgefährten an
  start_the_game: Spiel starten
  customize: Melodie
  switch_all: Alles umschalten
  open_aria_label: Offene Spielregeln
  close: Schließen
  hint_title: Beschreibung „{title}“
  category_hints:
    '101': Wenn diese Einstellung aktiviert ist, werden Sie manchmal auf Aufgaben stoßen, die leicht außerhalb der von Ihnen
      ausgewählten Kategorien liegen. Dies führt nicht zu einer wesentlichen Änderung der erhaltenen Aufgaben, bringt
      aber etwas Abwechslung in das Gameplay. Für das beste Spielerlebnis empfehlen wir, die Einstellung aktiviert zu
      lassen.
  unblock: Get

es:
  schema_title: Nuevas configuraciones del juego
  preview_alt: Personaliza el juego para ti y tu pareja.
  start_the_game: iniciar juego
  customize: Melodía
  switch_all: Alternar todo
  open_aria_label: reglas del juego abierto
  close: Cerca
  hint_title: Descripción "{title}"
  category_hints:
    '101': Cuando esta configuración está habilitada, a veces encontrará tareas que están ligeramente fuera de las
      categorías que ha seleccionado. Esto no supondrá un cambio significativo en las tareas recibidas, pero agregará un
      poco de variedad al juego. Recomendamos dejar la configuración habilitada para obtener la mejor experiencia de
      juego.
  unblock: Vamos.

fr:
  schema_title: Nouveaux paramètres de jeu
  preview_alt: Personnalisez le jeu pour vous et votre partenaire
  start_the_game: Commencer le jeu
  customize: Régler
  switch_all: Tout basculer
  open_aria_label: Règles du jeu ouvert
  close: Fermer
  hint_title: Description "{title}"
  category_hints:
    '101': Lorsque ce paramètre est activé, vous rencontrerez parfois des tâches légèrement en dehors des catégories que
      vous avez sélectionnées. Cela n'entraînera pas de changement significatif dans les tâches reçues, mais ajoutera un
      peu de variété au gameplay. Nous vous recommandons de laisser le paramètre activé pour une meilleure expérience de
      jeu.
  unblock: Obtenez
</i18n>

<style lang="scss">
.gameSettings {
  $self: &;

  flex: 1;
  background-color: black;

  &__header {
    top: 0;
    position: sticky;
    align-items: center;
    white-space: nowrap;
  }

  &__content {
    max-width: 1100px;
  }

  &__lock {
    transform-origin: 50% 50%;
    transform: scale(2);
    position: absolute;
    top: 0.2rem;
    right: 0.3rem;
    z-index: 1;
  }

  .p-card-content {
    padding-bottom: 0;
  }

  &__background {
    @media (max-width: 800px) {
      aspect-ratio: 1 / 1;
      height: auto;
      top: var(--header-height);
    }
  }

  &__additionalAction {
    width: 46px !important;
    flex: 1 0 46px !important;
    height: 46px !important;
  }
}
</style>
